import type {
    GridInteractionActions,
    GridInteractionCell,
    InteractionState,
} from '../types';

const DRAG_SETTLE_MS = 160;
const EMPTY_SCHEDULE_IDS: readonly number[] = Object.freeze([]);

function freezeCell(cell: GridInteractionCell): GridInteractionCell {
    return Object.freeze({ date: cell.date, timeSlot: cell.timeSlot });
}

function freezeScheduleIds(scheduleIds: readonly number[]): readonly number[] {
    return scheduleIds.length === 0 ? EMPTY_SCHEDULE_IDS : Object.freeze([...scheduleIds]);
}

function freezeSnapshot(snapshot: InteractionState): InteractionState {
    return Object.freeze(snapshot);
}

const IDLE_INTERACTION_STATE: InteractionState = freezeSnapshot({
    hoveredCell: null,
    activeDragTarget: null,
    hoveredDriverId: null,
    dragPhase: 'idle',
    dragSourceType: null,
    draggingScheduleIds: EMPTY_SCHEDULE_IDS,
    draggingDriverId: null,
});

type Listener = () => void;
type DragMotionPayload = Parameters<GridInteractionActions['startDragMotion']>[0];

function cellsEqual(left: GridInteractionCell | null, right: GridInteractionCell | null): boolean {
    return left === right
        || (left !== null && right !== null
            && left.date === right.date
            && left.timeSlot === right.timeSlot);
}

function numbersEqual(left: readonly number[], right: readonly number[]): boolean {
    return left === right
        || (left.length === right.length && left.every((value, index) => value === right[index]));
}

export interface GridInteractionStore {
    readonly actions: GridInteractionActions;
    cancelPendingTimers: () => void;
    getDraggingScheduleMembership: () => DraggingScheduleMembership;
    getSnapshot: () => InteractionState;
    subscribe: (listener: Listener) => () => void;
}

export interface DraggingScheduleMembership {
    readonly has: (scheduleId: number) => boolean;
}

function createDraggingScheduleMembership(scheduleIds: readonly number[]): DraggingScheduleMembership {
    const scheduleIdSet = new Set(scheduleIds);
    return Object.freeze({
        has: (scheduleId: number) => scheduleIdSet.has(scheduleId),
    });
}

export function createGridInteractionStore(): GridInteractionStore {
    let snapshot = IDLE_INTERACTION_STATE;
    let draggingScheduleMembership = createDraggingScheduleMembership(EMPTY_SCHEDULE_IDS);
    let pickupFrame: number | null = null;
    let settleTimer: ReturnType<typeof setTimeout> | null = null;
    const listeners = new Set<Listener>();

    const publish = (nextSnapshot: InteractionState) => {
        if (nextSnapshot === snapshot) {
            return;
        }

        const previousSnapshot = snapshot;
        snapshot = freezeSnapshot(nextSnapshot);
        if (previousSnapshot.draggingScheduleIds !== nextSnapshot.draggingScheduleIds) {
            draggingScheduleMembership = createDraggingScheduleMembership(nextSnapshot.draggingScheduleIds);
        }
        listeners.forEach(listener => listener());
    };

    const cancelPendingTimers = () => {
        if (pickupFrame !== null) {
            cancelAnimationFrame(pickupFrame);
            pickupFrame = null;
        }
        if (settleTimer !== null) {
            clearTimeout(settleTimer);
            settleTimer = null;
        }
    };

    const setHoveredCell = (cell: GridInteractionCell | null) => {
        if (cellsEqual(snapshot.hoveredCell, cell)) {
            return;
        }
        publish({ ...snapshot, hoveredCell: cell === null ? null : freezeCell(cell) });
    };

    const setActiveDragTarget = (target: GridInteractionCell | null) => {
        if (cellsEqual(snapshot.activeDragTarget, target)) {
            return;
        }
        publish({ ...snapshot, activeDragTarget: target === null ? null : freezeCell(target) });
    };

    const setTargetPair = (target: GridInteractionCell) => {
        if (cellsEqual(snapshot.hoveredCell, target) && cellsEqual(snapshot.activeDragTarget, target)) {
            return;
        }
        const ownedTarget = freezeCell(target);
        publish({ ...snapshot, hoveredCell: ownedTarget, activeDragTarget: ownedTarget });
    };

    const clearTargetPair = () => {
        if (snapshot.hoveredCell === null && snapshot.activeDragTarget === null) {
            return;
        }
        publish({ ...snapshot, hoveredCell: null, activeDragTarget: null });
    };

    const setHoveredDriverId = (driverId: number | null) => {
        if (snapshot.hoveredDriverId === driverId) {
            return;
        }
        publish({ ...snapshot, hoveredDriverId: driverId });
    };

    const clearDragMotion = () => {
        cancelPendingTimers();
        if (snapshot.dragPhase === 'idle'
            && snapshot.dragSourceType === null
            && snapshot.draggingDriverId === null
            && snapshot.draggingScheduleIds.length === 0) {
            return;
        }
        publish({
            ...snapshot,
            dragPhase: 'idle',
            dragSourceType: null,
            draggingScheduleIds: EMPTY_SCHEDULE_IDS,
            draggingDriverId: null,
        });
    };

    const startDragMotion = (payload: DragMotionPayload) => {
        cancelPendingTimers();
        const scheduleIds = payload.scheduleIds ?? EMPTY_SCHEDULE_IDS;
        const nextScheduleIds = numbersEqual(snapshot.draggingScheduleIds, scheduleIds)
            ? snapshot.draggingScheduleIds
            : freezeScheduleIds(scheduleIds);
        publish({
            ...snapshot,
            dragPhase: 'pickup',
            dragSourceType: payload.sourceType,
            draggingScheduleIds: nextScheduleIds,
            draggingDriverId: payload.driverId ?? null,
        });

        pickupFrame = requestAnimationFrame(() => {
            pickupFrame = null;
            if (snapshot.dragPhase !== 'pickup') {
                return;
            }
            publish({ ...snapshot, dragPhase: 'dragging' });
        });
    };

    const beginDragSettle = () => {
        cancelPendingTimers();
        if (!(snapshot.dragPhase === 'idle' && snapshot.dragSourceType === null)) {
            publish({ ...snapshot, dragPhase: 'drop-settle' });
        }
        settleTimer = setTimeout(() => {
            settleTimer = null;
            clearDragMotion();
        }, DRAG_SETTLE_MS);
    };

    const actions: GridInteractionActions = {
        setHoveredCell,
        setActiveDragTarget,
        setTargetPair,
        clearTargetPair,
        setHoveredDriverId,
        startDragMotion,
        beginDragSettle,
        clearDragMotion,
    };

    return {
        actions,
        cancelPendingTimers,
        getDraggingScheduleMembership: () => draggingScheduleMembership,
        getSnapshot: () => snapshot,
        subscribe: listener => {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
    };
}
